import React, { useEffect, useRef, useState } from 'react';
import { SKILLS } from '../data';

const CONSTELLATION_COLORS = {
  "Data Engineering": "#e8c547",
  "Analytics & BI": "#94a3b8",
  "AI & Machine Learning": "#c084fc",
  "Cloud & Infrastructure": "#67e8f9",
  "Programming": "#86efac",
  "Product & Strategy": "#fda4af",
};

export default function SkillConstellation() {
  const canvasRef = useRef(null);
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const hoveredCatRef = useRef(null);
  const animFrameRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const init = (W, H) => {
      cancelAnimationFrame(animFrameRef.current);
      if (cleanupRef.current) cleanupRef.current();

      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');

      const categories = Object.keys(SKILLS);
      const nodes = [];

      // Category nodes placed at 38% of min dimension — more spread
      const catRadius = Math.min(W, H) * 0.38;

      categories.forEach((cat, ci) => {
        const angle = (ci / categories.length) * Math.PI * 2 - Math.PI / 2;
        const cx = W / 2 + Math.cos(angle) * catRadius;
        const cy = H / 2 + Math.sin(angle) * catRadius;

        nodes.push({
          id: `cat-${ci}`, label: cat, x: cx, y: cy,
          isCategory: true, color: CONSTELLATION_COLORS[cat], cat,
        });

        // Skill nodes: spread orbit 50%–100% of catRadius from center
        SKILLS[cat].forEach((skill, si) => {
          const spread = SKILLS[cat].length;
          const sAngle = angle + ((si - spread / 2) * 0.3);
          const minDist = catRadius * 0.52;
          const maxDist = catRadius * 1.02;
          const sr = minDist + Math.random() * (maxDist - minDist);

          nodes.push({
            id: `${ci}-${si}`, label: skill,
            x: W / 2 + Math.cos(sAngle) * sr + (Math.random() - 0.5) * 45,
            y: H / 2 + Math.sin(sAngle) * sr + (Math.random() - 0.5) * 45,
            isCategory: false, color: CONSTELLATION_COLORS[cat],
            parentId: `cat-${ci}`, cat,
          });
        });
      });

      const onMouseMove = (e) => {
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        mouseRef.current = { x: mx, y: my };
        let found = null;
        nodes.forEach(n => {
          const dx = n.x - mx, dy = n.y - my;
          if (Math.sqrt(dx * dx + dy * dy) < (n.isCategory ? 24 : 18)) found = n;
        });
        hoveredCatRef.current = found ? found.cat : null;
        setHoveredSkill(found ? found.label : null);
      };

      const onMouseLeave = () => {
        mouseRef.current = { x: -1000, y: -1000 };
        hoveredCatRef.current = null;
        setHoveredSkill(null);
      };

      canvas.addEventListener('mousemove', onMouseMove);
      canvas.addEventListener('mouseleave', onMouseLeave);

      const getNightMode = () => document.body.classList.contains('mood-night');

      const draw = () => {
        ctx.clearRect(0, 0, W, H);
        const { x: mx, y: my } = mouseRef.current;
        const activeCat = hoveredCatRef.current;
        const isNight = getNightMode();
        const labelColor = isNight ? 'rgba(232,232,228,' : 'rgba(26,26,26,';

        // Edges
        nodes.filter(n => !n.isCategory).forEach(n => {
          const parent = nodes.find(p => p.id === n.parentId);
          if (!parent) return;
          const highlighted = activeCat === n.cat;
          ctx.beginPath();
          ctx.moveTo(parent.x, parent.y);
          ctx.lineTo(n.x, n.y);
          ctx.strokeStyle = `${n.color}${highlighted ? '55' : '1a'}`;
          ctx.lineWidth = highlighted ? 0.8 : 0.5;
          ctx.stroke();
        });

        // Nodes
        nodes.forEach(n => {
          const dx = n.x - mx, dy = n.y - my;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const isHovered = dist < (n.isCategory ? 24 : 18);
          const isCatHighlighted = !n.isCategory && activeCat === n.cat;

          const radius = n.isCategory
            ? (isHovered ? 14 : 10)
            : (isHovered || isCatHighlighted ? 7 : 4.5);

          ctx.beginPath();
          ctx.arc(n.x, n.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = n.isCategory
            ? n.color
            : (isHovered || isCatHighlighted ? n.color : `${n.color}70`);

          if (n.isCategory) {
            ctx.shadowColor = n.color;
            ctx.shadowBlur = isHovered ? 24 : 12;
          }
          ctx.fill();
          ctx.shadowBlur = 0;

          // Labels
          if (n.isCategory || isHovered || isCatHighlighted) {
            ctx.font = n.isCategory
              ? `500 12px 'DM Mono', monospace`
              : `300 10px 'DM Mono', monospace`;
            ctx.fillStyle = n.isCategory
              ? n.color
              : `${labelColor}${isHovered ? '0.9' : '0.6'})`;
            ctx.textAlign = n.x < W / 2 ? 'right' : 'left';
            const offset = n.isCategory ? 19 : 12;
            ctx.fillText(
              n.label,
              n.x + (n.x < W / 2 ? -offset : offset),
              n.y + (n.isCategory ? 4 : 3)
            );
          }
        });

        // Gentle float
        nodes.forEach(n => {
          n.x += Math.sin(Date.now() * 0.0003 + n.id.length) * 0.08;
          n.y += Math.cos(Date.now() * 0.0004 + n.id.length) * 0.08;
        });

        animFrameRef.current = requestAnimationFrame(draw);
      };

      draw();

      cleanupRef.current = () => {
        canvas.removeEventListener('mousemove', onMouseMove);
        canvas.removeEventListener('mouseleave', onMouseLeave);
        cancelAnimationFrame(animFrameRef.current);
      };
    };

    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          init(Math.floor(width), Math.floor(height));
        }
      }
    });
    ro.observe(canvas);

    return () => {
      ro.disconnect();
      if (cleanupRef.current) cleanupRef.current();
    };
  }, []);

  return (
    <section id="skills" style={{ padding: '80px 0', borderTop: '1px solid rgba(26,26,26,0.06)', background: 'var(--bg)' }}>
      <div style={{ padding: '0 12%' }}>
        <div className="fade-in" style={{ marginBottom: '48px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.28em', color: 'var(--ink-mid)', textTransform: 'uppercase', marginBottom: '16px' }}>
            Skills & Expertise
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 600, color: 'var(--ink)', lineHeight: 1, letterSpacing: '-0.03em', marginBottom: '12px' }}>
            The Constellation
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--ink-light)', fontWeight: 300 }}>
            Hover any node to explore. Each cluster is a domain of expertise.
          </p>
        </div>

        <div style={{ position: 'relative' }}>
          <canvas
            ref={canvasRef}
            style={{ width: '100%', height: 'clamp(340px, 75vh, 680px)', display: 'block', cursor: 'crosshair' }}
          />
          {hoveredSkill && (
            <div style={{
              position: 'absolute', top: '20px', left: '20px',
              fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.15em',
              color: 'var(--ink)', border: '1px solid rgba(26,26,26,0.15)',
              padding: '8px 16px', background: 'var(--bg)', textTransform: 'uppercase',
              pointerEvents: 'none',
            }}>
              {hoveredSkill}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: '32px' }}>
          {Object.entries(CONSTELLATION_COLORS).map(([cat, color]) => (
            <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.15em', color: 'var(--ink-light)', textTransform: 'uppercase' }}>{cat}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
